import React, { FC, useCallback, useMemo, useState } from 'react';
import { CheckOutlined, MinusOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { EmptyState } from '../empty_state/EmptyState';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';

require('./SubAssetSelectionStep.less');

interface SubAssetSelectionStepProps {
	parentAssetId: number;
	value: number[];
	onChange: (ids: number[]) => void;
	// Role-scoped fetcher provided by the parent wizard. Should resolve with
	// the sub-asset records for the given parent (filtered by parentIds).
	fetchSubAssets: (parentId: number) => Promise<any[]>;
}

const SubAssetCard: FC<{
	checked: boolean;
	indeterminate?: boolean;
	onClick: () => void;
	title: string;
	subtitle?: string;
}> = ({ checked, indeterminate, onClick, title, subtitle }) => {
	// Only highlight the card when fully checked. Indeterminate state still
	// shows the minus icon inside the box but leaves the outer card unhighlighted
	// so the partial selection reads as "not everything yet."
	const active = checked;
	return (
		<div>
			<div
				role="checkbox"
				aria-checked={indeterminate ? 'mixed' : checked}
				tabIndex={0}
				onClick={onClick}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onClick();
					}
				}}
				className={`radio-outer ${active ? 'checked' : ''}`}
			>
				<label>
					<span data-pop-class="radio-pop">
						<div>{title}</div>
						{subtitle ? (
							<div style={{ marginTop: 4, fontSize: 12, lineHeight: '20px', opacity: 0.7 }}>
								{subtitle}
							</div>
						) : null}
					</span>
				</label>
				<br />
				<div className="radio-box">
					<div className="radio-round">
						{checked ? (
							<CheckOutlined style={{ fontSize: 24 }} />
						) : indeterminate ? (
							<MinusOutlined style={{ fontSize: 24 }} />
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};

const SubAssetSelectionStep: FC<SubAssetSelectionStepProps> = ({
	parentAssetId,
	value,
	onChange,
	fetchSubAssets,
}) => {
	const [subAssets, setSubAssets] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);

	useMountEffect(() => {
		if (!parentAssetId) {
			setLoading(false);
			return;
		}
		fetchSubAssets(parentAssetId)
			.then((records = []) => setSubAssets(records))
			.catch(() => setSubAssets([]))
			.finally(() => setLoading(false));
	});

	const allIds = useMemo(
		() => subAssets.map((asset) => nullSafeGet('id', asset)).filter((id) => id !== undefined),
		[subAssets]
	);
	const allChecked = subAssets.length > 0 && value.length === subAssets.length;
	const someChecked = value.length > 0 && value.length < subAssets.length;

	const toggleAll = useCallback(
		() => onChange(allChecked ? [] : allIds),
		[allChecked, allIds, onChange]
	);

	const toggleOne = useCallback(
		(id: number) =>
			value.includes(id)
				? onChange(value.filter((existingId) => existingId !== id))
				: onChange([...value, id]),
		[onChange, value]
	);

	if (loading) {
		return (
			<div className="flex justify-center py-8">
				<Spin />
			</div>
		);
	}

	if (subAssets.length === 0) {
		return (
			<EmptyState
				headline={'No sub assets'}
				body={
					<div style={{ textAlign: 'center', maxWidth: 440 }}>
						This asset doesn't have any sub assets to select from.
					</div>
				}
			/>
		);
	}

	return (
		<div className="subAssetSelection">
			<div className="radio-container radio-container--center">
				<SubAssetCard
					checked={allChecked}
					indeterminate={someChecked}
					onClick={toggleAll}
					title="All Sub Assets"
				/>
				{subAssets.map((asset) => {
					const id = nullSafeGet('id', asset);
					const name = nullSafeGet('name', asset) || '(unnamed)';
					const assetNumber = nullSafeGet('assetNumber', asset);
					return (
						<SubAssetCard
							key={id}
							checked={value.includes(id)}
							onClick={() => toggleOne(id)}
							title={name}
							subtitle={assetNumber ? `#${assetNumber}` : undefined}
						/>
					);
				})}
			</div>
		</div>
	);
};

export default SubAssetSelectionStep;
